/**
 * StickyNoteColorPicker
 *
 * A single color dot trigger that opens a dropdown menu of pastel color options.
 * Selected color shows as the trigger dot; selected dropdown option has a thicker border.
 */

import { memo, useCallback, useState, useRef } from 'react';
import { Modal, Pressable, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { Ionicons } from '@expo/vector-icons';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { Colors } from '@/constants/theme';

const DOT_SIZE = 28;

export const TASK_NOTE_COLORS = [
  '#FFFFFF', // white (default)
  '#FFF9C4', // yellow
  '#FFCCBC', // peach
  '#E1BEE7', // lavender
  '#C8E6C9', // mint
  '#C5CAE9', // periwinkle
] as const;

// Dark mode display equivalents for color picker visual preview
// These map to what the colors will actually look like in dark mode
export const DARK_MODE_DISPLAY_COLORS: Record<string, string> = {
  '#FFFFFF': '#1A1726', // white → dark purple tint (cardBackground)
  '#FFF9C4': '#4A4535', // yellow → muted dark yellow
  '#FFCCBC': '#4A3D35', // peach → muted dark peach
  '#E1BEE7': '#3D3545', // lavender → muted dark lavender
  '#C8E6C9': '#354538', // mint → muted dark mint
  '#C5CAE9': '#353D4A', // periwinkle → muted dark periwinkle
};

export const DEFAULT_TASK_NOTE_COLOR = TASK_NOTE_COLORS[0]; // white

type StickyNoteColorPickerProps = {
  selectedColor: string;
  onColorSelected: (color: string) => void;
  iconColor?: string;
};

const TRIGGER_SIZE = 24;

export const StickyNoteColorPicker = memo(function StickyNoteColorPicker({
  selectedColor,
  onColorSelected,
  iconColor = '#555',
}: StickyNoteColorPickerProps) {
  const colorScheme = useColorScheme() ?? 'light';
  const palette = Colors[colorScheme];
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<View>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });

  const handleOpenDropdown = useCallback(() => {
    triggerRef.current?.measureInWindow((x, y, width, height) => {
      setDropdownPosition({ x: x - (DOT_SIZE * 6 + Spacing.sm * 5 - width), y: y + height + Spacing.xs });
      setIsOpen(true);
    });
  }, []);

  const handleSelectColor = useCallback((color: string) => {
    onColorSelected(color);
    setIsOpen(false);
  }, [onColorSelected]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // In dark mode, show the visual equivalent of what the color will look like
  const displaySelectedColor = colorScheme === 'dark'
    ? (DARK_MODE_DISPLAY_COLORS[selectedColor] ?? selectedColor)
    : selectedColor;

  return (
    <>
      {/* Trigger dot showing current color */}
      <TouchableOpacity
        onPress={handleOpenDropdown}
        activeOpacity={0.7}
      >
        <View
          ref={triggerRef}
          style={[styles.trigger, { backgroundColor: displaySelectedColor }]}
        >
          <Ionicons name="color-fill-outline" size={14} color={iconColor} />
        </View>
      </TouchableOpacity>

      {/* Dropdown overlay */}
      <Modal visible={isOpen} transparent animationType="fade" onRequestClose={handleClose}>
        <Pressable style={styles.backdrop} onPress={handleClose}>
          <View
            style={[
              styles.dropdown,
              { top: dropdownPosition.y, left: dropdownPosition.x, backgroundColor: palette.cardBackground },
            ]}
          >
            {TASK_NOTE_COLORS.map((color) => (
              <ColorOption
                key={color}
                color={color}
                isSelected={selectedColor === color}
                onPress={handleSelectColor}
                colorScheme={colorScheme}
              />
            ))}
          </View>
        </Pressable>
      </Modal>
    </>
  );
});

type ColorOptionProps = {
  color: string;
  isSelected: boolean;
  onPress: (color: string) => void;
  colorScheme: 'light' | 'dark';
};

const ColorOption = memo(function ColorOption({ color, isSelected, onPress, colorScheme }: ColorOptionProps) {
  const handlePress = useCallback(() => {
    onPress(color);
  }, [color, onPress]);

  // In dark mode, show the visual equivalent of what the color will look like
  const displayColor = colorScheme === 'dark'
    ? (DARK_MODE_DISPLAY_COLORS[color] ?? color)
    : color;

  return (
    <TouchableOpacity
      onPress={handlePress}
      activeOpacity={0.7}
      style={[
        styles.dot,
        { backgroundColor: displayColor },
        isSelected && styles.dotSelected,
      ]}
    />
  );
});

const styles = StyleSheet.create({
  trigger: {
    width: TRIGGER_SIZE,
    height: TRIGGER_SIZE,
    borderRadius: TRIGGER_SIZE / 2,
    borderWidth: 1,
    borderColor: '#00000020',
    alignItems: 'center',
    justifyContent: 'center',
  },
  backdrop: {
    flex: 1,
  },
  dropdown: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    // backgroundColor applied dynamically via palette.cardBackground
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderRadius: BorderRadius.xl,
    // Shadow
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.18,
    shadowRadius: 6,
    elevation: 6,
  },
  dot: {
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotSelected: {
    borderColor: 'rgba(0, 0, 0, 0.3)',
    borderWidth: 2,
  },
});
